/**
 * 移动端图片预览手势工具
 * 支持双指缩放（pinch）和单指拖动（drag），松手接近 1:1 时自动回正
 *
 * 使用示例：
 *   const handler = createPinchZoomHandler({ onChange: (transform) => store.transform = transform });
 *   <div onTouchStart={handler.touchStart} onTouchMove={handler.touchMove} onTouchEnd={handler.touchEnd}>
 *     <img style={{ transform: handler.getTransform() }} />
 *   </div>
 */

var MIN_SCALE = 0.5;
var MAX_SCALE = 5;
var RESTORE_THRESHOLD = 0.3;

/**
 * 计算两个触摸点之间的欧几里得距离
 */
function getDistance(t1, t2) {
  var dx = t1.clientX - t2.clientX;
  var dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 创建初始触摸状态
 */
function createInitialState() {
  return {
    mode: null,
    initialDistance: 0,
    initialScale: 1,
    baseScale: 1,
    baseTranslateX: 0,
    baseTranslateY: 0,
    startTouchX: 0,
    startTouchY: 0,
    moved: false,
    currentTX: 0,
    currentTY: 0
  };
}

/**
 * 构造 transform 字符串
 */
function buildTransform(scale, tx, ty) {
  return 'scale(' + scale + ') translate(' + tx + 'px, ' + ty + 'px)';
}

var INITIAL_TRANSFORM = buildTransform(1, 0, 0);

/**
 * 创建 pinch-zoom 手势处理器
 *
 * @param {Object} options
 * @param {Function} options.onChange - transform 变化时的回调，参数为新的 transform 字符串
 * @param {number} [options.minScale=0.5] - 最小缩放比例
 * @param {number} [options.maxScale=5] - 最大缩放比例
 * @param {number} [options.restoreThreshold=0.3] - 缩放回正阈值
 * @returns {Object} 包含 touchStart/touchMove/touchEnd/getTransform/reset/isMoved 的处理器对象
 */
function createPinchZoomHandler(options) {
  var onChange = options.onChange || function() {};
  var minScale = options.minScale !== undefined ? options.minScale : MIN_SCALE;
  var maxScale = options.maxScale !== undefined ? options.maxScale : MAX_SCALE;
  var restoreThreshold = options.restoreThreshold !== undefined ? options.restoreThreshold : RESTORE_THRESHOLD;

  var state = createInitialState();

  function emit(scale, tx, ty) {
    onChange(buildTransform(scale, tx, ty));
  }

  return {
    /**
     * 触摸开始事件
     * - 双指触摸：进入 pinch 缩放模式，记录初始两指距离和当前缩放基准
     * - 单指触摸：进入 drag 拖动模式，记录触摸起始坐标和当前平移基准
     */
    touchStart: function(e) {
      var touches = e.touches;
      state.moved = false;

      if (touches.length === 2) {
        e.preventDefault();
        state.mode = 'pinch';
        state.initialDistance = getDistance(touches[0], touches[1]);
        state.baseScale = state.initialScale;
      } else if (touches.length === 1) {
        state.mode = 'drag';
        state.startTouchX = touches[0].clientX;
        state.startTouchY = touches[0].clientY;
        state.baseTranslateX = state.currentTX;
        state.baseTranslateY = state.currentTY;
      }
    },

    /**
     * 触摸移动事件
     * - pinch 模式：根据两指距离变化计算新的缩放比例，并限制在 minScale ~ maxScale 范围内
     * - drag 模式：根据手指移动距离计算新的平移量（需要除以当前缩放比例进行坐标系换算）
     */
    touchMove: function(e) {
      e.preventDefault();
      var touches = e.touches;

      if (state.mode === 'pinch' && touches.length === 2) {
        state.moved = true;
        var newDist = getDistance(touches[0], touches[1]);
        var scale = Math.min(maxScale, Math.max(minScale, state.baseScale * (newDist / state.initialDistance)));
        state.initialScale = scale;
        emit(scale, state.baseTranslateX, state.baseTranslateY);
      } else if (state.mode === 'drag' && touches.length === 1) {
        state.moved = true;
        var dx = touches[0].clientX - state.startTouchX;
        var dy = touches[0].clientY - state.startTouchY;
        var tx = state.baseTranslateX + dx / state.initialScale;
        var ty = state.baseTranslateY + dy / state.initialScale;
        state.currentTX = tx;
        state.currentTY = ty;
        emit(state.initialScale, tx, ty);
      }
    },

    /**
     * 触摸结束事件
     * - 双指缩放结束但仍有单指：切换为 drag 模式，以便继续拖动
     * - 所有手指抬起：若缩放比例接近 1:1（在阈值范围内），自动回正到原始大小
     */
    touchEnd: function(e) {
      if (state.mode === 'pinch' && e.touches.length < 2) {
        state.mode = e.touches.length === 1 ? 'drag' : null;
        if (e.touches.length === 1) {
          state.startTouchX = e.touches[0].clientX;
          state.startTouchY = e.touches[0].clientY;
          state.baseTranslateX = state.currentTX;
          state.baseTranslateY = state.currentTY;
        }
        return;
      }
      if (state.initialScale > 1 + restoreThreshold || state.initialScale < 1 - restoreThreshold) {
        return;
      }
      state.initialScale = 1;
      state.baseTranslateX = 0;
      state.baseTranslateY = 0;
      state.currentTX = 0;
      state.currentTY = 0;
      emit(1, 0, 0);
    },

    /** 获取初始 transform 字符串 */
    getInitialTransform: function() {
      return INITIAL_TRANSFORM;
    },

    /** 判断本次触摸是否发生了移动（用于区分点击和拖动） */
    isMoved: function() {
      return state.moved;
    },

    /** 清除 moved 标记 */
    clearMoved: function() {
      state.moved = false;
    },

    /** 重置所有状态到初始值，关闭预览时调用 */
    reset: function() {
      state = createInitialState();
    }
  };
}

export default createPinchZoomHandler;
